import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { RouteErrorBoundary } from "./ErrorBoundary";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDate, statutLabel } from "../lib/format";

function NaxMark({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <line x1="72" y1="22" x2="28" y2="78" stroke="#bcd2b2" strokeWidth="2.2" />
      <line x1="28" y1="22" x2="28" y2="78" stroke="#AADB4C" strokeWidth="6" strokeLinecap="round" />
      <line x1="72" y1="22" x2="72" y2="78" stroke="#AADB4C" strokeWidth="6" strokeLinecap="round" />
      <line x1="28" y1="22" x2="72" y2="78" stroke="#AADB4C" strokeWidth="6" strokeLinecap="round" />
      {[[28, 22], [72, 22], [28, 78], [72, 78]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="6.6" fill="#00524A" stroke="#AADB4C" strokeWidth="4" />
      ))}
      <circle cx="50" cy="50" r="9.7" fill="#AADB4C" />
      <circle cx="50" cy="50" r="3.3" fill="#00524A" />
    </svg>
  );
}

const SECTIONS: { title: string; items: { to: string; label: string; countKey?: string; managerOnly?: boolean }[] }[] = [
  { title: "", items: [{ to: "/", label: "Dashboard" }] },
  {
    title: "Répertoire",
    items: [
      { to: "/repertoire", label: "Répertoire", countKey: "organisations" },
    ],
  },
  {
    title: "Commercial",
    items: [
      { to: "/devis", label: "Devis", countKey: "devis" },
      { to: "/pipeline", label: "Pipeline", countKey: "pipeline" },
      { to: "/leads", label: "Leads", countKey: "leads" },
      { to: "/poc", label: "POC", countKey: "poc" },
    ],
  },
  { title: "Récurrent", items: [{ to: "/recurrent", label: "Récurrent", countKey: "recurrent" }] },
  { title: "Service", items: [{ to: "/tickets", label: "Tickets", countKey: "tickets" }] },
  {
    title: "Gestion",
    items: [
      { to: "/tresorerie", label: "Trésorerie", countKey: "tresorerie", managerOnly: true },
      { to: "/bons-commande", label: "Bons de commande", countKey: "bons", managerOnly: true },
    ],
  },
  {
    title: "Data",
    items: [
      { to: "/import", label: "Import", managerOnly: true },
      { to: "/export", label: "Export", managerOnly: true },
    ],
  },
  { title: "Paramétrage", items: [{ to: "/parametrage", label: "Paramétrage", managerOnly: true }] },
];

const GROUP_LABELS: Record<string, string> = {
  organisations: "Organisations",
  contacts: "Contacts",
  devis: "Devis",
  contrats: "Contrats",
  kpax: "KPAX",
  leads: "Leads",
  tickets: "Tickets",
  bons_commande: "Bons de commande",
};

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
        setOpen(true);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (group: string, item: any) => {
    setOpen(false);
    setQ("");
    const routes: Record<string, string> = {
      organisations: `/repertoire?org=${item.id}`,
      contacts: `/repertoire?contact=${item.id}&tab=contacts`,
      devis: `/devis/${item.id}`,
      contrats: `/recurrent?contrat=${item.id}`,
      kpax: `/recurrent?tab=kpax&env=${item.id}`,
      leads: `/leads?lead=${item.id}`,
      tickets: `/tickets?ticket=${item.id}`,
      bons_commande: `/bons-commande?bon=${item.id}`,
    };
    nav(routes[group] || "/");
  };

  return (
    <div className="relative w-full max-w-md" ref={ref}>
      <svg className="pointer-events-none absolute left-3 top-2.5 text-muted" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4-4" /></svg>
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results && setOpen(true)}
        placeholder="Recherche globale (2 caractères min.)…"
        className="nx-input pl-9"
      />
      {open && results && (
        <div data-testid="global-search-results" className="nx-fade-up absolute z-40 mt-1 max-h-96 w-full overflow-y-auto rounded-xl border border-line bg-white p-2 shadow-lg">
          {Object.keys(results).length === 0 && <p className="p-3 text-sm text-muted">Aucun résultat</p>}
          {Object.entries(results).map(([group, items]: [string, any]) => (
            <div key={group} className="mb-2">
              <p className="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-muted">{GROUP_LABELS[group] || group}</p>
              {items.map((item: any) => (
                <button
                  key={item.id}
                  data-testid={`search-result-${group}`}
                  onClick={() => go(group, item)}
                  className="block w-full rounded-lg px-2 py-1.5 text-left text-sm hover:bg-soft"
                >
                  <span className="font-semibold text-ink">
                    {item.nom || item.objet || item.client_nom || item.reference || (item.numero ? `N° ${item.numero}` : "")}
                    {group === "contacts" && ` ${item.prenom || ""} ${item.nom || ""}`}
                  </span>
                  <span className="ml-2 text-xs text-muted">
                    {item.statut ? statutLabel(item.statut) : item.type || item.email || item.pays || ""}
                    {item.numero && group === "devis" ? ` · N° ${item.numero}` : ""}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuickAdd() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const items = [
    { label: "Organisation", to: "/repertoire?new=org" },
    { label: "Contact", to: "/repertoire?new=contact&tab=contacts" },
    { label: "Devis", to: "/devis/nouveau" },
    { label: "Lead", to: "/leads?new=1" },
    { label: "Contrat", to: "/recurrent?new=1" },
    { label: "Ticket", to: "/tickets?new=1" },
  ];
  return (
    <div className="relative" ref={ref}>
      <button data-testid="quick-add-btn" onClick={() => setOpen(!open)} className="nx-btn-lime">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
        Ajouter
      </button>
      {open && (
        <div className="nx-fade-up absolute right-0 z-40 mt-1 w-44 rounded-xl border border-line bg-white p-1.5 shadow-lg">
          {items.map((i) => (
            <button
              key={i.label}
              data-testid={`quick-add-${i.label.toLowerCase()}`}
              onClick={() => {
                setOpen(false);
                nav(i.to);
              }}
              className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium hover:bg-soft"
            >
              {i.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const nav = useNavigate();

  useEffect(() => {
    const load = () => api.get("/counts").then((r) => setCounts(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const isManager = user?.role === "manager";

  return (
    <div className="flex min-h-screen">
      <aside
        data-testid="sidebar"
        className={`sticky top-0 flex h-screen flex-col bg-green text-white transition-all duration-200 ${collapsed ? "w-16" : "w-60"}`}
      >
        <div className="flex items-center gap-2.5 px-4 py-4">
          <NaxMark />
          {!collapsed && <span className="text-lg font-extrabold tracking-wide">NAXIUM</span>}
          <button
            data-testid="sidebar-toggle"
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto rounded-lg p-1 text-white/60 hover:bg-white/10 hover:text-white"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d={collapsed ? "m9 18 6-6-6-6" : "m15 18-6-6 6-6"} /></svg>
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 pb-4">
          {SECTIONS.map((s) => {
            const items = s.items.filter((i) => !i.managerOnly || isManager);
            if (!items.length) return null;
            return (
              <div key={s.title || "root"} className="mb-1">
                {s.title && !collapsed && (
                  <p className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-widest text-white/40">{s.title}</p>
                )}
                {items.map((i) => (
                  <NavLink
                    key={i.to}
                    to={i.to}
                    end={i.to === "/"}
                    data-testid={`nav-${i.to === "/" ? "dashboard" : i.to.slice(1)}`}
                    className={({ isActive }) =>
                      `mb-0.5 flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        isActive ? "bg-lime text-green-deep" : "text-white/85 hover:bg-white/10"
                      }`
                    }
                  >
                    <span className={collapsed ? "mx-auto text-xs" : ""}>{collapsed ? i.label.slice(0, 2) : i.label}</span>
                    {!collapsed && i.countKey && counts[i.countKey] !== undefined && (
                      <span className="rounded-full bg-white/15 px-1.5 text-[11px] font-bold">{counts[i.countKey]}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lime text-xs font-extrabold text-green-deep">
              {user?.initiales}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{user?.nom_complet}</p>
                <p className="text-[11px] capitalize text-white/60" data-testid="user-role">{user?.role}</p>
              </div>
            )}
            <button
              data-testid="logout-btn"
              title="Déconnexion"
              onClick={() => {
                logout();
                nav("/login");
              }}
              className="rounded-lg p-1.5 text-white/60 hover:bg-white/10 hover:text-white"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
            </button>
          </div>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-4 border-b border-line bg-paper/90 px-6 py-3 backdrop-blur">
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-3">
            <QuickAdd />
          </div>
        </header>
        <main className="flex-1 p-6">
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </main>
      </div>
    </div>
  );
}
